import {
  countByStatus,
  useGetAllBibliographies,
} from "@/api/bibliography/bibliography";
import NewBibliographyDialog from "@/components/bibliography/NewBibliographyDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  bibliographyAdvancedSearchConfig,
  bibliographyColumns,
} from "@/config/dashboard/bibliography";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Bibliography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "bibliography-table-preferences";
const flatVisibleBibliographyColumns = bibliographyColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const BibliographyDashboard = () => {
  const { t } = useTranslation();

  const {
    countByStatus: count,
    currentCount: currentCount,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Bibliography>({
    resourceName: "bibliography",
    resourceCounter: countByStatus,
    statusField: "bibliographiePublicationStatus",
  });

  const navigate = useNavigate();

  const { data } = useGetAllBibliographies(fetchParams);

  const columns: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return generateTable(bibliographyColumns, "bibliography");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleBibliographyColumns;
    }
    return flatVisibleBibliographyColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...bibliographyColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...bibliographyColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleBibliographies")}
        </p>
        <GlobalSearch
          config={bibliographyAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={count}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={bibliographyAdvancedSearchConfig}
            resource="bibliography"
          />
          <AdvancedSort config={bibliographyAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createBbibliography")}
            Dialog={NewBibliographyDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) =>
            navigate("/edit/bibliography/" + row.original.id)
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
        />
      </div>
    </div>
  );
};

export default BibliographyDashboard;
